import { FC, useCallback, useState } from 'react';

import { Copy } from 'lucide-react';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import css from './TemporalIdDialog.module.css';

import { userService } from 'v2/api/userService';
import { TournamentSelect } from 'v2/components/select/tournamentSelect/TournamentSelect';
import { UserModel } from 'v2/types/UserModel';
import { copyText, handleHttpError } from 'v2/utils';

interface TemporalIdDialogProps {
  user: UserModel;
  isOpen: boolean;
  onClose: VoidFunction;
}

export const TemporalIdDialog: FC<TemporalIdDialogProps> = ({ user, isOpen, onClose }) => {
  const [selectedTournamentId, setSelectedTournamentId] = useState('');

  const {
    data: temporalId,
    trigger,
    error,
    reset,
  } = useSWRMutation(['getTemporalId', user.id], userService.getTemporalId, {
    onError: handleHttpError,
  });

  const handleChange = useCallback(
    (tournamentId: string) => {
      setSelectedTournamentId(tournamentId);
      trigger(tournamentId);
    },
    [trigger]
  );

  const handleClose = () => {
    onClose();
    setSelectedTournamentId('');
    reset();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{user.userNickname} Temporal Id</DialogTitle>

      <DialogContent className={css.temporalIdDialogContent}>
        <TournamentSelect value={selectedTournamentId} onChange={handleChange} />

        {!error && temporalId && (
          <>
            <Button
              onClick={() => copyText(temporalId?.balanceId)}
              endIcon={<Copy width={22} />}
              variant="outlined"
              size="large"
            >
              <span className="truncate">{temporalId.balanceId}</span>
            </Button>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
